/**
 * Per-note frame evaluation shared by the quad pass, the ghost pass and read-time checks (W1-06; R§4.3, R§4.4,
 * R§4.6, R§10 `pushNoteQuads`, S§3.2-S§3.4).
 *
 * For note n on line L at chart time t (all products rounded to f32 the way Phira stores them):
 *   dH      = n.height - L.height(t)
 *   key     = (dH + n.yOff / n.speed) * 450                      control-curve key (RPE-y px, ignores speed/yControl)
 *   ctrl    = alpha/size/pos/y control curves at key (absent -> 1)
 *   spd     = n.speed * ctrlY
 *   base    = dH * spd / ar                                      world distance along local +y
 *   tr      = (n.x * (hold ? 1 : inclineVal * ctrlPos), n.yOff / ar + (hold ? 0 : base))
 *   S       = (size * ctrlSize, noteUniformScale ? size * ctrlSize : 1)
 *   model   = lineMatrix * (above ? I : Scale(1, -1)) * Translate(tr) * Scale(S)
 * Non-holds are one quad x in [-w, w], y in [-w h/w_tex, +w h/w_tex] with w = 0.13175016 * noteScale (times
 * click_mh.w / click.w when multi-highlighted). Holds are body, head and tail quads (holds.ts). Colour: tint
 * bytes with alpha = max(noteAlpha(t), 0) * resAlpha * ctrlAlpha (* cover fade for non-holds on cover lines,
 * * 0.5 for a missed hold in judged play), then clamped explicitly to a byte.
 */

import type { EngineCompileContext } from '../types';
import { CONTROL_KEY_SCALE, INCLINE_DIVISOR } from './constants';
import type { FrameEnv, LineEnv, NoteEval } from './env';
import { baseSlotOf, SLOT_MH_OFFSET, texSize, writeCorners } from './env';
import { colorByte, quadOffscreen } from './geometry';
import { holdQuads } from './holds';
import { NOTE_HOLD, type LineNotesRt } from './runtime';
import { coverFade, coverHides, isMissed, noteAlphaAt } from './visibility';

const fr = Math.fround;

/** Absolute and relative safety margins of the early cull (world units; f32 rounding is far below them). */
const EARLY_CULL_ABS = 1e-3;
const EARLY_CULL_REL = 1e-5;

/**
 * Conservative early cull of a note the walk visits (W3-02a, UX-025): true only when EVERY quad `evalNote` would
 * produce for note i lies beyond the same edge of the [-1, 1]² cull square (and of the visible area
 * [-1, 1] x [-1/ar, 1/ar]) by a safety margin, so Phira's off-screen test culls each of them and the note draws
 * nothing; the ghost pass would not list it either (its outline lies outside the visible area). Answers false whenever that cannot be shown cheaply: the line has control curves that
 * move or resize the note (`LineEnv.cullHolds` / `cullOthers`), or any value is not finite.
 *
 * Bounds: the note's line-local rect x in [x - w, x + w], y in [lo, hi] with w = |size| * the largest note half
 * width, and for holds lo / hi = the body ends (bottom is 0 once the head passed) widened by the largest head / tail
 * height, for other notes the centre `yOff / ar + dH * speed / ar` widened by the largest half height; mirrored for
 * below notes, then mapped by the line matrix. Every quad corner lies inside that rect, and a half-plane that holds
 * all four corners of the rect holds every point of it.
 */
export function surelyOffscreen(fe: FrameEnv, le: LineEnv, rt: LineNotesRt, i: number): boolean {
  const hold = rt.type[i] === NOTE_HOLD;
  if (!(hold ? le.cullHolds : le.cullOthers)) return false;
  const k = (rt.speed[i] ?? Number.NaN) / fe.ar;
  const size = rt.size[i] ?? Number.NaN;
  // Without sizeControl the note scale is (size, noteUniformScale ? size : 1).
  const sy = fe.noteUniformScale ? size : 1;
  const yOffAr = (rt.yOff[i] ?? Number.NaN) / fe.ar;
  const lineH = le.lineHeight;
  const height = rt.height[i] ?? Number.NaN;
  let yMin: number;
  let yMax: number;
  let mag: number;
  if (hold) {
    // Body from `start` to `end` (scaled by sy), head and tail outside it.
    const start = fe.t >= (rt.time[i] ?? Number.NaN) ? 0 : (height - lineH) * k;
    const endH = rt.endHeight[i] ?? Number.NaN;
    const end = (endH - lineH) * k;
    const lo = Math.min(start, end) - fe.boundHoldPad;
    const hi = Math.max(start, end) + fe.boundHoldPad;
    const a = yOffAr + sy * lo;
    const b = yOffAr + sy * hi;
    yMin = Math.min(a, b);
    yMax = Math.max(a, b);
    mag = (Math.abs(height) + Math.abs(endH) + Math.abs(lineH)) * Math.abs(k) * Math.abs(sy);
  } else {
    // Centred on yOff / ar + dH * speed / ar (not scaled), half height scaled by sy.
    const centre = yOffAr + (height - lineH) * k;
    const hh = Math.abs(sy) * fe.boundHalfH;
    yMin = centre - hh;
    yMax = centre + hh;
    mag = (Math.abs(height) + Math.abs(lineH)) * Math.abs(k);
  }
  const fy = rt.above[i] === 1 ? 1 : -1;
  const ly0 = fy * yMin;
  const ly1 = fy * yMax;
  const x = rt.x[i] ?? Number.NaN;
  const w = Math.abs(size) * fe.boundHalfW;
  const lx0 = x - w;
  const lx1 = x + w;
  const bound = 1 + EARLY_CULL_ABS + EARLY_CULL_REL * (mag + Math.abs(yOffAr) + Math.abs(x) + w + 1);
  // y: the cull square's +-1, or the visible +-1/ar when that is larger (portrait: the ghost pass lists notes there).
  const yBound = Math.max(bound, bound / fe.ar);
  const [a, b, c, d, e, f] = le.m;
  const x0 = a * lx0 + c * ly0 + e;
  const y0 = b * lx0 + d * ly0 + f;
  const x1 = a * lx1 + c * ly0 + e;
  const y1 = b * lx1 + d * ly0 + f;
  const x2 = a * lx1 + c * ly1 + e;
  const y2 = b * lx1 + d * ly1 + f;
  const x3 = a * lx0 + c * ly1 + e;
  const y3 = b * lx0 + d * ly1 + f;
  return (
    (x0 > bound && x1 > bound && x2 > bound && x3 > bound) ||
    (x0 < -bound && x1 < -bound && x2 < -bound && x3 < -bound) ||
    (y0 > yBound && y1 > yBound && y2 > yBound && y3 > yBound) ||
    (y0 < -yBound && y1 < -yBound && y2 < -yBound && y3 < -yBound)
  );
}

/**
 * Evaluates note `i` of `le.rt` at the frame time. Geometry is computed unless the note is covered and
 * `geometryWhenCovered` is false. Rules 1-4 and 7 are the caller's business (line rules, judged, culling).
 */
export function evalNote(
  ctx: EngineCompileContext,
  fe: FrameEnv,
  le: LineEnv,
  i: number,
  geometryWhenCovered: boolean,
  out: NoteEval,
): NoteEval {
  const rt = le.rt;
  const t = fe.t;
  const hold = rt.type[i] === NOTE_HOLD;
  const height = rt.height[i] ?? 0;
  const speed = rt.speed[i] ?? 0;
  const yOff = rt.yOff[i] ?? 0;
  const dH = fr(height - le.lineHeight);
  let cA = 1;
  let cS = 1;
  let cP = 1;
  let cYraw: number | null = null;
  if (!le.noControls) {
    const key = fr(fr(dH + fr(yOff / speed)) * CONTROL_KEY_SCALE);
    cA = ctx.control(le.line, 'alpha', key) ?? 1;
    cS = ctx.control(le.line, 'size', key) ?? 1;
    cP = ctx.control(le.line, 'pos', key) ?? 1;
    cYraw = ctx.control(le.line, 'y', key);
  }
  const cY = cYraw ?? 1;
  out.ctrlY = cYraw;
  const spd = fr(speed * cY);
  const base = fr(fr(dH * spd) / fe.ar);
  const coverBase =
    hold && fe.holdPartialCover ? fr(fr(fr((rt.endHeight[i] ?? 0) - le.lineHeight) * spd) / fe.ar) : base;
  out.covered = !le.drawBelow && coverHides(rt, i, t, coverBase);
  out.quads = 0;
  if (out.covered && !geometryWhenCovered) return out;

  // Colour (R§4.4).
  const noteAlpha = noteAlphaAt(rt, i, t);
  out.beforeVisible = noteAlpha === 0 && (rt.alpha[i] ?? 0) > 0;
  let a = Math.max(noteAlpha, 0) * fe.resAlpha * cA;
  if (!hold && !le.drawBelow) a *= coverFade(rt, i, t);
  if (hold && isMissed(rt, i, t, fe.judge)) a *= 0.5;
  a *= le.alphaMul;
  out.alpha = a;
  out.alphaByte = colorByte(a);
  out.r = rt.tint[i * 3] ?? 255;
  out.g = rt.tint[i * 3 + 1] ?? 255;
  out.b = rt.tint[i * 3 + 2] ?? 255;

  // Geometry.
  const mh = fe.player.doubleHint && rt.mh[i] === 1;
  const scale = mh ? fe.scaleMH : fe.scaleNormal;
  const fy = rt.above[i] === 1 ? 1 : -1;
  const size = rt.size[i] ?? 0;
  const sx = fr(size * cS);
  const sy = fe.noteUniformScale ? sx : 1;
  const yOffAr = fr(yOff / fe.ar);
  if (!hold) {
    const slot = baseSlotOf(rt.type[i] ?? 1) + (mh ? SLOT_MH_OFFSET : 0);
    const [tw, th] = texSize(fe.skin, slot);
    const hh = (scale * th) / tw;
    const inclineVal = fr(
      1 - fr(fr(fr(le.inclineSin * fr(fr(dH * spd) + yOff)) * CONTROL_KEY_SCALE) / INCLINE_DIVISOR),
    );
    const tx = fr(fr((rt.x[i] ?? 0) * inclineVal) * cP);
    const ty = fr(yOffAr + base);
    out.localY = ty;
    writeCorners(le, fy, tx, ty, sx, sy, -scale, scale, -hh, hh, out.pos, 0);
    out.uv[0] = 0;
    out.uv[1] = 0;
    out.uv[2] = 1;
    out.uv[3] = 1;
    out.slot[0] = slot;
    out.offscreen[0] = quadOffscreen(out.pos, 0) ? 1 : 0;
    out.quads = 1;
    out.outline.set(out.pos.subarray(0, 8));
    return out;
  }
  holdQuads(fe, le, i, spd, scale, mh, fy, sx, sy, yOffAr, out);
  return out;
}
